import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";

// Minimal animated spinner, all inline!
function Spinner() {
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "220px",
      paddingTop: 72
    }}>
      <div style={{
        width: 46,
        height: 46,
        border: "6px solid #c7d8ea",
        borderTop: "6px solid #222",
        borderRadius: "50%",
        animation: "spin 1s linear infinite"
      }} />
      <div style={{
        marginTop: 15,
        color: "#222",
        fontWeight: 600,
        fontSize: "1.15em",
      }}>
        Loading...
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg);}
            100% { transform: rotate(360deg);}
          }
        `}
      </style>
    </div>
  );
}

function CollapsiblePanel({ title, open, onClick, children }) {
  return (
    <div style={{ marginBottom: 7 }}>
      <div
        onClick={onClick}
        style={{
          background: "#222",
          color: "#fff",
          fontWeight: 700,
          fontSize: "1.25em",
          borderRadius: 8,
          padding: "10px 22px",
          marginBottom: 2,
          cursor: "pointer",
          letterSpacing: 0.5,
          userSelect: "none",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span style={{ fontWeight: 900, marginRight: 10 }}>{open ? "−" : "+"}</span>
        <span>{title}</span>
      </div>
      {open && (
        <div style={{
          background: "#f7f7f7",
          borderRadius: "0 0 8px 8px",
          padding: 18,
          border: "1px solid #eee",
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

function CollapsibleTable({ title, children, open, onClick }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        onClick={onClick}
        style={{
          background: "#444",
          color: "#fff",
          fontWeight: 700,
          fontSize: "1.08em",
          borderRadius: 6,
          padding: "8px 18px",
          marginBottom: 2,
          cursor: "pointer",
          letterSpacing: 0.4,
          userSelect: "none",
        }}
      >
        <span style={{ fontWeight: 900, marginRight: 8 }}>{open ? "−" : "+"}</span>
        <span>{title}</span>
      </div>
      {open && (
        <div style={{
          background: "#fff",
          borderRadius: "0 0 8px 8px",
          padding: 10,
          border: "1px solid #eee",
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

function SessionTables({ session }) {
  const [open, setOpen] = useState([true, true, true]);
  return (
    <>
      <CollapsibleTable
        title="Participants by Event"
        open={open[0]}
        onClick={() => setOpen(([a, b, c]) => [!a, b, c])}
      >
        <table style={{ width: "100%", margin: "10px 0", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f7f7f7" }}>
              <th>Event</th>
              <th>No.</th>
            </tr>
          </thead>
          <tbody>
            {session.participants?.map((p, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                <td>{p.event}</td>
                <td>{p.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleTable>
      <CollapsibleTable
        title="Medal Tally"
        open={open[1]}
        onClick={() => setOpen(([a, b, c]) => [a, !b, c])}
      >
        <table style={{ width: "100%", margin: "10px 0", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f7f7f7" }}>
              <th>Team</th>
              <th>Gold</th>
              <th>Silver</th>
              <th>Bronze</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {session.medalTally?.map((m, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                <td>{m.team}</td>
                <td>{m.gold}</td>
                <td>{m.silver}</td>
                <td>{m.bronze}</td>
                <td>{m.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleTable>
      <CollapsibleTable
        title="Group Wise Participants"
        open={open[2]}
        onClick={() => setOpen(([a, b, c]) => [a, b, !c])}
      >
        <table style={{ width: "100%", margin: "10px 0", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f7f7f7" }}>
              <th>Event</th>
              <th>U14 Boys</th>
              <th>U14 Girls</th>
              <th>U17 Boys</th>
              <th>U17 Girls</th>
              <th>U19 Boys</th>
              <th>U19 Girls</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {session.groupWiseParticipants?.map((g, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                <td>{g.event}</td>
                <td>{g.u14Boys}</td>
                <td>{g.u14Girls}</td>
                <td>{g.u17Boys}</td>
                <td>{g.u17Girls}</td>
                <td>{g.u19Boys}</td>
                <td>{g.u19Girls}</td>
                <td>{g.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleTable>
    </>
  );
}

function KshetraDetailPage() {
  const { remark } = useParams();
  const [kshetra, setKshetra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openArchive, setOpenArchive] = useState(0); // Only one archive open

  // refs for each collapsible panel in the session archive
  const archiveRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError("");
    fetch(`/api/kshetra/${encodeURIComponent(remark)}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch Kshetra");
        return res.json();
      })
      .then((data) => {
        setKshetra(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [remark]);

  if (loading) return <Spinner />;
  if (error) return <p style={{ color: "red", textAlign: "center" }}>{error}</p>;
  if (!kshetra) return <p style={{ color: "red", textAlign: "center" }}>Kshetra not found.</p>;

  const info = kshetra.kshetraInfo || {};
  const sessions = kshetra.sessions || [];
  const latestSession = sessions.length > 0 ? sessions[0] : null;

  // Members render
  const renderMembers = () => (
    <table style={{ width: "100%", margin: "16px 0", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "#f7f7f7" }}>
          <th>Name</th>
          <th>Designation</th>
          <th>Profession</th>
          <th>Contact</th>
          <th>Photo</th>
        </tr>
      </thead>
      <tbody>
        {kshetra.members?.map((mem, idx) => (
          <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
            <td>{mem.name}</td>
            <td>{mem.designation}</td>
            <td>{mem.profession}</td>
            <td>
              {mem.phone && <div>☎️ {mem.phone}</div>}
              {mem.mobileNumber && <div>📱 {mem.mobileNumber}</div>}
              {mem.emailID && <div>✉️ {mem.emailID}</div>}
            </td>
            <td>
              {mem.picture ? (
                <img
                  src={mem.picture}
                  alt={mem.name}
                  style={{ width: 48, height: 48, borderRadius: "50%" }}
                />
              ) : (
                <span>No photo</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div style={{
      maxWidth: 1100,
      margin: "80px auto 0 auto",
      padding: "2rem 1.5rem",
      background: "#fff",
      borderRadius: "16px",
      boxShadow: "0 2px 18px rgba(0,0,0,0.07)"
    }}>
      <Link to="/affiliated-units" style={{ color: "#0073e6", textDecoration: "none", fontSize: "0.95em" }}>← Back to Units</Link>
      <h2 style={{ margin: "1em 0 0.4em 0", fontWeight: 800, fontSize: "2rem" }}>
        {info.name}
      </h2>
      <div style={{ marginBottom: "1.4em" }}>
        <div><strong>Address:</strong> {info.address || "N/A"}</div>
        <div>
          <strong>Contact:</strong>{" "}
          {info.phone ? `Phone: ${info.phone}` : ""}
          {(info.phone && info.mobile) ? ", " : ""}
          {info.mobile ? `Mobile: ${info.mobile}` : ""}
        </div>
        <div><strong>Email:</strong> {info.email || "N/A"}</div>
        <div><strong>Unit ID:</strong> {remark}</div>
      </div>

      {/* Session and Panels */}
      <div style={{ display: "flex", gap: "1.2rem", marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{
          flex: 1, minWidth: 180, background: "#e9f4ff", padding: "1.2em", borderRadius: 10,
          textAlign: "center"
        }}>
          <div style={{ fontSize: "1.1em", color: "#0476d9", fontWeight: 700 }}>Session</div>
          <div style={{ fontSize: "2.1em", fontWeight: 800 }}>
            {latestSession ? latestSession.session : "-"}
          </div>
        </div>
        <div style={{
          flex: 1, minWidth: 180, background: "#f4faeb", padding: "1.2em", borderRadius: 10,
          textAlign: "center"
        }}>
          <div style={{ fontSize: "1.1em", color: "#53a507", fontWeight: 700 }}>Total Events</div>
          <div style={{ fontSize: "2.1em", fontWeight: 800 }}>
            {latestSession?.summary?.totalEvents ?? 0}
          </div>
        </div>
        <div style={{
          flex: 1, minWidth: 180, background: "#fff2f2", padding: "1.2em", borderRadius: 10,
          textAlign: "center"
        }}>
          <div style={{ fontSize: "1.1em", color: "#d54b4b", fontWeight: 700 }}>Total Participants</div>
          <div style={{ fontSize: "2.1em", fontWeight: 800 }}>
            {latestSession?.summary?.totalParticipants ?? 0}
          </div>
        </div>
      </div>

      {/* Members */}
      {kshetra.members && kshetra.members.length > 0 && (
        <section>
          <h3>Members</h3>
          {renderMembers()}
        </section>
      )}

      {/* Archive: Session-wise panels */}
      <section style={{ marginTop: 40 }}>
        <h3>Session Archive</h3>
        <div>
          {sessions.map((sess, idx) => (
            <div
              key={sess.session}
              ref={el => archiveRefs.current[idx] = el}
            >
              <CollapsiblePanel
                title={sess.session}
                open={openArchive === idx}
                onClick={() => {
                  if (openArchive !== idx && archiveRefs.current[idx]) {
                    setOpenArchive(idx);
                    setTimeout(() => {
                      // If you have a fixed navbar, adjust yOffset accordingly (e.g., -60)
                      const yOffset = -10;
                      const y = archiveRefs.current[idx].getBoundingClientRect().top + window.pageYOffset + yOffset;
                      window.scrollTo({ top: y, behavior: "smooth" });
                    }, 180);
                  } else {
                    setOpenArchive(openArchive === idx ? -1 : idx);
                  }
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  Events: {sess.summary.totalEvents} | Participants: {sess.summary.totalParticipants}
                </div>
                <SessionTables session={sess} />
              </CollapsiblePanel>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default KshetraDetailPage;
